import { ConditionParams } from "@multiweb/clients/dataReader";
import { IAttrValue } from "@multiweb/types/contracts/dataReader";

export const MULTI_OBJECT_ID: string = "-1";
export const MULTI_OBJECT_INSTANCE_PREFIX: string = "meow_";

/** Výsledek průniku dvou editačních skupin */
export type IntersectResult = { id: string; group: IMEChangeGroup };

/** Reprezentuje změnu hodnoty atributu v režimu MultiEditace */
export type MEAttrChange = { readonly order: number; readonly attrName: string; readonly value: IAttrValue };
/** Reprezentuje instanci v MultiEditaci a její seznam změn */
export type MEInstance = { id: string; values: readonly MEAttrChange[] };
/** Seznam změn pro MultiEditaci */
export type MEChanges = { changedInstances?: MEInstance[] | undefined; newGroups?: IMEChangeGroup[] | undefined; remGroups?: IMEChangeGroup[] | undefined };

/**
 * Rozhraní, které agreguje změny vybraných skupin a poskytuje aktuální data pro zobrazení na formuláři
 */
export interface IMEChangeStack {
    /** Instance pro které se aplikují změny přímo */
    readonly groups: readonly IMEChangeGroup[];
    /** Seznam skupin se změnami, které nejsou aplikovány instančně */
    readonly instances: IMEInstanceChangeProp[];
    /** Třída pro kterou se změny budou aplikovat*/
    readonly className: string;
}

/**
 * Rozhraní které popisuje provedené změny na konkrétní instanci
 */
export interface IMEInstanceChangeProp {
    /** ID objektu */
    readonly id: string;
    /** ID instance */
    readonly instanceId: string;
    /** Změny atributů dané instance*/
    readonly changes: MEAttrChange[];
}

/**
 * Skupina změn pro MultiEditaci
 */
export interface IMEChangeGroup {
    /** Seznam změněných atributů */
    changes: readonly MEAttrChange[];
    /** Indikátor, zda má skupina nějakou změnu */
    readonly hasChanges: boolean;
    /** Seznam excludovaných instancí */
    excluded: readonly string[];
    /** Přidá instanci do skupiny */
    include(instanceId: string): void;
    /** Vyjme instanci ze skupiny */
    exclude(instanceId: string): void;
    /** Přidá jednu změnu do skupiny */
    addChange(attrName: string, value: IAttrValue): void;

}

export class IMEChangeGroup {
    private static _properties: (keyof (IMEChangeGroup))[] = ["hasChanges", "changes", "excluded"];
    private static _methods: (keyof (IMEChangeGroup))[] = ["include", "exclude", "addChange"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/** Skupina změn zadaná podmínkou */
export interface IMEConditionGroup extends IMEChangeGroup {
    /** Podmínka pro výběr objektů */
    readonly condition: string;
    /** Parametry podmínky pro výběr objektů */
    readonly conditionParams: ConditionParams;
    /** Vrací seznam změn vzhledem k předchozímu stavu  */
    merge(existingInstances: readonly string[], intersectsInstances: readonly string[]): MEChanges;
}

export class IMEConditionGroup {
    private static _properties: (keyof (IMEConditionGroup))[] = ["condition"];
    private static _methods: (keyof (IMERecordsGroup))[] = ["merge"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") && instance instanceof IMEChangeGroup;
    }
}

/**
 * Skupina změn zadaná seznamem instancí
 */
export interface IMERecordsGroup extends IMEChangeGroup {
    /** Seznam instancí ve skupině */
    instances: readonly string[];
    /** Vrací seznam změn vzhledem k předchozímu stavu  */
    merge(existingInstances: readonly string[], intersectsGroups: readonly IntersectResult[]): MEChanges;
}

export class IMERecordsGroup {
    private static _properties: (keyof (IMERecordsGroup))[] = ["instances"];
    private static _methods: (keyof (IMERecordsGroup))[] = ["merge"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") && instance instanceof IMEChangeGroup;
    }
}
